<template>
  <div class="w-full h-full flex flex-col font-light select-none transition-colors duration-200"
    :class="isDark ? 'bg-zinc-950 text-zinc-400' : 'bg-white text-zinc-600'">
    
    <!-- En-tête des jours (Sticky) -->
    <div class="grid grid-cols-[65px_1fr] sticky top-0 left-0 w-full z-30 shrink-0 border-b transition-colors duration-200"
      :class="isDark ? 'bg-zinc-950 border-zinc-900' : 'bg-white border-zinc-100/80 shadow-sm shadow-zinc-100/20'">
      <div class="border-r transition-colors duration-200" :class="isDark ? 'border-zinc-900/60' : 'border-zinc-100/60'"></div>
      <div class="grid" :class="gridColsClass">
        <div
          v-for="(day, index) in eventsByDay"
          :key="index"
          class="p-3 flex flex-col items-center justify-center text-center gap-1 border-l transition-colors duration-200"
          :class="isDark ? 'border-zinc-900/40 first:border-l-0' : 'border-zinc-100/40 first:border-l-0'"
        >
          <span 
            class="text-[10px] uppercase tracking-widest font-medium transition-colors duration-200"
            :class="[
              isToday(day.date) 
                ? (isDark ? 'text-[#D5CCBC]' : 'text-zinc-900 font-semibold') 
                : 'text-zinc-500'
            ]"
          >
            {{ new Date(day.date).toLocaleDateString("fr-FR", { weekday: "short" }) }}
          </span>
          <span 
            class="text-sm font-normal tracking-tight font-mono transition-all duration-200"
            :class="[
              isToday(day.date)
                ? (isDark ? 'text-white bg-zinc-900 px-2 py-0.5 rounded-lg border border-zinc-800' : 'text-zinc-900 bg-zinc-100 px-2 py-0.5 rounded-lg font-medium')
                : (isDark ? 'text-zinc-300' : 'text-zinc-400')
            ]"
          >
            {{ new Date(day.date).toLocaleDateString("fr-FR", { day: "numeric" }) }}
          </span>
        </div>
      </div>
    </div>

    <!-- Grille horaire + Événements -->
    <div class="grid w-full overflow-y-auto max-h-[82vh] relative grid-cols-[65px_1fr] no-scrollbar">
      
      <!-- Colonne des Heures latérale -->
      <div class="flex flex-col border-r relative z-10 transition-colors duration-200" 
        :class="isDark ? 'border-zinc-900 bg-zinc-950/60' : 'border-zinc-100 bg-white/80 backdrop-blur-sm'">
        <div
          v-for="hour in hours"
          :key="`hour-label-${hour}`"
          class="h-[100px] sm:h-[120px] px-2 flex items-start justify-end text-[10px] font-medium font-mono pt-1.5 border-t transition-colors duration-200"
          :class="[
            isDark 
              ? 'text-zinc-600 border-zinc-900/60 first:border-t-0' 
              : 'text-zinc-400 border-zinc-100/60 first:border-t-0'
          ]"
        >
          {{ formatHour(hour) }}
        </div>
      </div>

      <!-- Corps de la grille principale -->
      <div class="grid relative transition-colors duration-200" 
        :class="[isDark ? 'bg-zinc-950/20' : 'bg-zinc-50/10', gridColsClass]">
        
        <!-- Indicateur de l'heure actuelle (Time tracker) -->
        <div
          ref="nowIndicator"
          class="absolute left-0 right-0 h-[1px] z-20 flex items-center pointer-events-none transition-colors duration-200"
          :class="isDark ? 'bg-[#D5CCBC]' : 'bg-zinc-900'"
          :style="nowIndicatorStyle"
        >
          <div class="absolute left-[-65px] text-[9px] font-mono font-medium px-1.5 py-0.5 rounded shadow-sm tracking-wide whitespace-nowrap transition-colors duration-200"
            :class="isDark ? 'bg-[#D5CCBC] text-black' : 'bg-zinc-900 text-white'">
            {{ nowLabel }}
          </div>
          <div class="w-1.5 h-1.5 rounded-full absolute left-0 -translate-x-1/2 transition-colors duration-200"
            :class="isDark ? 'bg-[#D5CCBC]' : 'bg-zinc-900'"></div>
        </div>

        <!-- Colonnes des Jours -->
        <div
          v-for="(day, dayIndex) in eventsByDay"
          :key="`day-${dayIndex}`"
          class="relative border-l min-h-[600px] day-column first:border-l-0 transition-colors duration-200"
          :class="isDark ? 'border-zinc-900/60' : 'border-zinc-100/60'"
        >
          <!-- Cellules Horaires cliquables -->
          <div
            v-for="hour in hours"
            :key="`hour-${dayIndex}-${hour}`"
            class="h-[100px] sm:h-[120px] p-1 border-t first:border-t-0 transition-colors duration-150 cursor-crosshair"
            :class="[
              isDark 
                ? 'border-zinc-900/40 hover:bg-zinc-900/30' 
                : 'border-zinc-100/40 hover:bg-zinc-50'
            ]"
            @click="handleCalendarClick(dayIndex, hour)"
          ></div>

          <!-- Blocs Rendez-vous (Events) -->
          <div
            v-for="event in day.events"
            :key="`event-${dayIndex}-${event.id}`"
            class="event-block resizable-event absolute rounded-xl p-2.5 border w-[94%] mx-auto left-0 right-0 cursor-pointer z-10 overflow-hidden transition-all duration-300 hover:scale-[1.01]"
            :data-id="event.id"
            :style="getEventStyle(event)"
            :class="[
              isDark
                ? 'backdrop-blur-md shadow-xl shadow-black/20 hover:border-zinc-600'
                : 'shadow-sm hover:shadow-md hover:border-zinc-300',
              event.status === 'Validé'
                ? (isDark ? 'bg-zinc-900/90 border-emerald-900/80 text-emerald-400' : 'bg-emerald-50/70 border-emerald-200/60 text-emerald-800') :
              event.status === 'En attente'
                ? (isDark ? 'bg-zinc-900/90 border-amber-900/80 text-amber-400' : 'bg-amber-50/60 border-amber-200/60 text-amber-800') :
                  (isDark ? 'bg-zinc-900/90 border-zinc-800 text-zinc-300' : 'bg-zinc-50 border-zinc-200/80 text-zinc-700')
            ]"
            @click="openSidebar(event)"
          >
            <!-- Indicateur de couleur de l'employé -->
            <div
              class="absolute left-0 top-0 bottom-0 w-1 rounded-l-xl"
              :style="{ backgroundColor: event.employeColor }"
            ></div>

            <div class="space-y-1 h-full flex flex-col justify-between text-left">
              <div class="min-w-0">
                <p class="text-[11px] font-medium truncate tracking-wide transition-colors duration-200"
                  :class="isDark ? 'text-white' : 'text-zinc-900'">
                  {{ event.nom }}
                </p>
                <p class="text-[10px] truncate font-light mt-0.5 transition-colors duration-200"
                  :class="isDark ? 'text-zinc-500' : 'text-zinc-500/90'">
                  {{ event.service.map(s => s.nom).join(', ') }}
                </p>
              </div>
              
              <!-- Pied du bloc RDV -->
              <div class="flex flex-wrap items-center justify-between gap-1 mt-1 pt-1.5 border-t shrink-0 transition-colors duration-200"
                :class="isDark ? 'border-zinc-900/60' : 'border-zinc-100/80'">
                <span class="text-[10px] font-mono font-medium transition-colors duration-200"
                  :class="isDark ? 'text-[#D5CCBC]' : 'text-zinc-900'">
                  {{ formatHourMinute(event.start) }}
                </span>
                <span 
                  class="text-[9px] font-medium uppercase tracking-wider px-1.5 py-0.5 rounded-md select-none truncate max-w-full font-mono shrink-0 transition-colors duration-200"
                  :class="[
                    event.status === 'Validé' 
                      ? (isDark ? 'bg-emerald-950/60 text-emerald-400 border border-emerald-900/40' : 'bg-emerald-100 text-emerald-800 font-semibold') :
                    event.status === 'En attente' 
                      ? (isDark ? 'bg-amber-950/60 text-amber-400 border border-amber-900/40' : 'bg-amber-100 text-amber-800 font-semibold') : 
                        (isDark ? 'bg-zinc-900 text-zinc-400' : 'bg-zinc-200 text-zinc-700')
                  ]"
                >
                  {{ event.status }}
                </span>
              </div>
            </div>
            
            <!-- Poignée de redimensionnement discrète -->
            <div class="resize-handle w-full h-1 absolute bottom-0 left-0 cursor-ns-resize transition-colors rounded-b-xl"
              :class="isDark ? 'bg-zinc-800/80 hover:bg-[#D5CCBC]' : 'bg-zinc-200/40 hover:bg-zinc-800'"></div>
          </div>
        </div>

        <!-- Tooltip volant lors du resize -->
        <div
          v-if="resizeTooltip.visible"
          class="absolute z-50 font-mono text-[10px] px-2 py-1 rounded-lg shadow-2xl pointer-events-none transition-all duration-100 border"
          :class="isDark ? 'bg-zinc-900 border-zinc-800 text-white' : 'bg-zinc-900 text-white border-zinc-950'"
          :style="{ top: `${resizeTooltip.y}px`, left: `${resizeTooltip.x}px` }"
        >
          {{ formatEventTime(resizeTooltip.previewEnd) }}
        </div>
      </div>
    </div>

    <EventSidebar
      :visible="showSidebar"
      :event="selectedEvent"
      @close="closeSidebar"
    />

  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from "vue";
import EventSidebar from './EventSidebar.vue';
import api from '@/api';
import { toast } from 'vue3-toastify';
import { useTheme } from '@/composables/useTheme';
import { usePermissions } from '@/composables/usePermissions';

const { isDark } = useTheme();
const { userRole } = usePermissions();
const showSidebar = ref(false);
const selectedEvent = ref(null);
const showModal = ref(false);
const resizeTooltip = reactive({ visible: false, x: 0, y: 0, previewEnd: null });

function openSidebar(event) {
  selectedEvent.value = event;
  showSidebar.value = true;
}

function closeSidebar() {
  showSidebar.value = false;
  selectedEvent.value = null;
}

function getLocalDate(daysOffset = 0) {
  const d = new Date();
  d.setDate(d.getDate() + daysOffset);
  d.setHours(0, 0, 0, 0);
  return d;
}

function isToday(dateString) {
  return new Date(dateString).toDateString() === new Date().toDateString();
}

const eventsByDay = reactive([
  { date: getLocalDate(0), events: [] },
  { date: getLocalDate(1), events: [] },
  { date: getLocalDate(2), events: [] },
  { date: getLocalDate(3), events: [] },
  { date: getLocalDate(4), events: [] },
  { date: getLocalDate(5), events: [] },
  { date: getLocalDate(6), events: [] },
]);

const loadWeekAppointments = async () => {
  try {
    const response = await api.appointments.getAllAppointments();
    const appointments = response.data || [];

    eventsByDay.forEach(day => day.events = []);

    const now = new Date();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay() + 1);
    startOfWeek.setHours(0, 0, 0, 0);

    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    endOfWeek.setHours(23, 59, 59, 999);

    // Récupérer l'ID de l'utilisateur connecté pour filtrer si collaborateur
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const isCollaborateur = userRole.value === 'collaborateur';
    const currentUserId = user.id || user._id;

    appointments.forEach(apt => {
      const aptDate = new Date(apt.start || apt.date_rdv);

      // Si collaborateur, ne montrer que ses propres rendez-vous
      if (isCollaborateur) {
        const employeId = apt.employe?._id || apt.employe?.id || apt.employe;
        if (employeId !== currentUserId) {
          return; // Ignorer ce rendez-vous
        }
      }

      if (aptDate >= startOfWeek && aptDate <= endOfWeek) {
        const dayOffset = Math.floor((aptDate - startOfWeek) / (1000 * 60 * 60 * 24));

        if (dayOffset >= 0 && dayOffset < 7) {
          const endDate = apt.end ? new Date(apt.end) : new Date(aptDate.getTime() + 60 * 60 * 1000);

          eventsByDay[dayOffset].events.push({
            id: apt.id || apt._id,
            nom: apt.nom || 'Client',
            telephone: apt.telephone || '',
            email: apt.email || '',
            service: apt.service || [],
            start: aptDate,
            end: endDate,
            status: apt.status || 'En attente',
            employe: apt.employe || {},
            employeColor: apt.employe?.couleur || '#6b7280', // Utiliser la couleur du staff
            paiement: apt.paiement || 'Non payé',
            notes: apt.notes || ''
          });
        }
      }
    });

  } catch (error) {
    console.error('Erreur lors du chargement des rendez-vous:', error);
    toast.error('Erreur lors du chargement du calendrier', { position: 'top-right' });
  }
};

const hours = Array.from({ length: 12 }, (_, i) => 8 + i);
const gridColsClass = computed(() => `grid-cols-${eventsByDay.length}`);

function formatHour(hour) {
  return `${hour}:00`;
}

function formatHourMinute(date) {
  const d = new Date(date);
  return `${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}`;
}

function formatEventTime(date) {
  const d = new Date(date);
  return `${d.getHours()}:${d.getMinutes()}`;
}

function handleCalendarClick(dayIndex, hour) {
  const dayDate = new Date(eventsByDay[dayIndex].date);
  selectedEvent.value = { start: new Date(dayDate.setHours(hour, 0)), service: [] };
  showModal.value = true;
}

function getEventStyle(event) {
  const start = new Date(`${event.start}`);
  const end = new Date(`${event.end}`);
  const top = ((start.getHours() + start.getMinutes() / 60 - 8) * 100); 
  const height = ((end - start) / (1000 * 60 * 60)) * 100;
  return { top: `${top}px`, height: `${height}px` };
}

const nowIndicator = ref(null);
const nowIndicatorStyle = reactive({ top: "0px" });
const nowLabel = ref("");

function updateNowIndicator() {
  const now = new Date();
  const top = ((now.getHours() + now.getMinutes() / 60 - 8) * 100);
  nowIndicatorStyle.top = `${top}px`;
  nowLabel.value = `${now.getHours()}:${now.getMinutes().toString().padStart(2, "0")}`;
}

onMounted(() => {
  setInterval(updateNowIndicator, 60000);
  updateNowIndicator();
  loadWeekAppointments();
});
</script>

<style scoped>
.no-scrollbar::-webkit-scrollbar {
  display: none;
}
.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.day-column {
  contain: paint;
}
.event-block {
  transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out, border-color 0.2s ease-in-out;
}
</style>